import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import type { LevelDTO } from "@/modules/master/domain/contracts";
import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";

type LevelRow = LevelDTO;

type LevelsTableProps = {
  data: LevelRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (level: LevelRow) => void;
  onDelete: (id: string) => void;
};

export function LevelsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDelete,
}: LevelsTableProps) {
  const columns: TanstackReactTable.ColumnDef<LevelRow>[] = [
    {
      header: "ລຳດັບ",
      accessorKey: "order",
      cell: ({ row }) => row.original.order ?? "-",
    },
    createSortableColumn("name", "ຊື່"),
    {
      header: "ຄຳອະທິບາຍ",
      accessorKey: "description",
      cell: ({ row }) => (
        <div className="max-w-md truncate">
          {row.original.description || "-"}
        </div>
      ),
    },
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge variant={status === "active" ? "default" : "secondary"}>
            {status === "active" ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
          </Badge>
        );
      },
    },
    {
      header: "ວັນທີສ້າງ",
      accessorKey: "createdAt",
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }: { row: { original: LevelRow } }) => {
        const { id } = row.original;
        const actions = [];

        actions.push({
          label: "ແກ້ໄຂ",
          icon: <EditIcon className="h-4 w-4" />,
          onClick: () => onEdit(row.original),
        });

        actions.push({
          label: "ລຶບ",
          variant: "destructive" as const,
          icon: <TrashIcon className="h-4 w-4" />,
          onClick: async () => {
            const ok = await confirm({
              title: "ລຶບລະດັບ",
              description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບລະດັບນີ້?",
              actionText: "ລຶບ",
              ActionProps: {
                variant: "destructive",
              },
            });
            if (ok) onDelete(id);
          },
        });

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<LevelRow, unknown>
      noDataMessage="ບໍ່ພົບລະດັບ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
